import React, { useState } from 'react';
import { format, addDays, startOfWeek, isSameDay } from 'date-fns';
import { Clock, Check, X, Calendar, RefreshCw } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';

const QuickWorkSchedule = ({ onClose }) => {
  const { currentUser, addActivityToCalendar } = useFamilyContext();
  const [selectedDays, setSelectedDays] = useState({});
  const [activeShift, setActiveShift] = useState('2-close');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentWeek, setCurrentWeek] = useState('this'); // 'this' or 'next'
  const [customHours, setCustomHours] = useState({
    startHour: 9,
    endHour: 17
  });
  
  // Get week's dates starting from Tuesday
  const baseDate = currentWeek === 'next' ? addDays(new Date(), 7) : new Date();
  const weekStart = startOfWeek(baseDate, { weekStartsOn: 2 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  
  // Hour options for custom shifts (full hours only)
  const hourOptions = Array.from({ length: 24 }, (_, i) => i);
  
  const formatHour = (hour) => {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);
    return format(date, 'h:mm a');
  };

  const shiftPresets = {
    '2-close': {
      label: '2PM - Close',
      startTime: '14:00',
      endTime: '22:00',
      color: 'bg-teal-500',
      icon: '🌅'
    },
    '5-close': {
      label: '5PM - Close', 
      startTime: '17:00',
      endTime: '22:00',
      color: 'bg-teal-600',
      icon: '🌆'
    },
    'custom': {
      label: 'Custom Hours',
      color: 'bg-stone-500',
      icon: '⏰'
    }
  };

  const dayLabels = ['Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Monday'];

  const toggleDay = (dayIndex) => {
    setSelectedDays(prev => ({
      ...prev,
      [dayIndex]: prev[dayIndex] ? null : activeShift
    }));
  };

  const handleWeekChange = (week) => {
    setCurrentWeek(week);
    setSelectedDays({}); // Clear selections when changing weeks
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Convert selected days to calendar events
      const promises = Object.entries(selectedDays)
        .filter(([, shift]) => shift)
        .map(async ([dayIndex, shift]) => {
          const date = weekDays[parseInt(dayIndex)];
          const preset = shiftPresets[shift];
          
          let startTime, endTime, timeLabel;
          
          if (shift === 'custom') {
            // Validate custom hours
            if (customHours.startHour >= customHours.endHour) {
              throw new Error('End time must be after start time');
            }
            
            // Use custom hours
            startTime = `${customHours.startHour.toString().padStart(2, '0')}:00`;
            endTime = `${customHours.endHour.toString().padStart(2, '0')}:00`;
            timeLabel = `${formatHour(customHours.startHour)} - ${formatHour(customHours.endHour)}`;
          } else if (preset.startTime && preset.endTime) {
            // Use preset times
            startTime = preset.startTime;
            endTime = preset.endTime;
            timeLabel = preset.label;
          } else {
            return null; // Skip invalid shifts
          }
          
          const startDateTime = new Date(date);
          const [startHour, startMin] = startTime.split(':');
          startDateTime.setHours(parseInt(startHour), parseInt(startMin), 0, 0);
          
          const endDateTime = new Date(date);
          const [endHour, endMin] = endTime.split(':');
          endDateTime.setHours(parseInt(endHour), parseInt(endMin), 0, 0);
          
          return addActivityToCalendar({
            title: `${currentUser?.first_name || currentUser?.name || 'Work'} Work Shift`,
            description: `${timeLabel} shift`,
            date: format(date, 'yyyy-MM-dd'), // Required date field
            time: `${startTime}-${endTime}`, // Required time field
            start_time: startDateTime.toISOString(),
            end_time: endDateTime.toISOString(),
            type: 'work-shift',
            category: 'work',
            priority: 'medium',
            kidId: currentUser.id,
            createdBy: currentUser.id,
            activityType: 'work-shift'
          });
        });
      
      await Promise.all(promises.filter(Boolean));
      onClose?.();
    } catch (error) {
      console.error('Error saving work schedule:', error);
      alert(error.message || 'Error saving work schedule');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedCount = () => Object.values(selectedDays).filter(Boolean).length;
  
  const isValidSubmission = () => {
    const hasSelectedDays = getSelectedCount() > 0;
    if (!hasSelectedDays) return false;
    
    // Check if custom shift is selected and validate hours
    if (activeShift === 'custom') {
      return customHours.startHour < customHours.endHour;
    }
    
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-700 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Clock className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold">Work Schedule</h2>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Week Toggle */}
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => handleWeekChange('this')}
              className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                currentWeek === 'this' 
                  ? 'bg-white/30 text-white' 
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              This Week
            </button>
            <button
              onClick={() => handleWeekChange('next')}
              className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                currentWeek === 'next' 
                  ? 'bg-white/30 text-white' 
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              Next Week
            </button>
          </div>
          
          <p className="text-teal-100 text-sm">
            {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Shift Type Selector */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 text-sm uppercase tracking-wide">
              Select Shift Type
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(shiftPresets).map(([key, preset]) => (
                <button
                  key={key}
                  onClick={() => setActiveShift(key)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    activeShift === key
                      ? 'border-teal-500 bg-teal-50 shadow-md'
                      : 'border-stone-200 bg-white hover:border-stone-300'
                  }`}
                >
                  <div className="text-2xl mb-2">{preset.icon}</div>
                  <div className="text-sm font-semibold text-slate-900">
                    {preset.label}
                  </div>
                </button>
              ))}
            </div>
            
            {/* Custom Hours Selector */}
            {activeShift === 'custom' && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1 font-medium">Start Time</label>
                    <select
                      value={customHours.startHour}
                      onChange={(e) => setCustomHours({...customHours, startHour: parseInt(e.target.value)})}
                      className="w-full text-sm border border-stone-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    >
                      {hourOptions.map(hour => (
                        <option key={hour} value={hour}>{formatHour(hour)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1 font-medium">End Time</label>
                    <select
                      value={customHours.endHour}
                      onChange={(e) => setCustomHours({...customHours, endHour: parseInt(e.target.value)})}
                      className="w-full text-sm border border-stone-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    >
                      {hourOptions.map(hour => (
                        <option key={hour} value={hour}>{formatHour(hour)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {customHours.startHour >= customHours.endHour && (
                  <p className="text-xs text-red-500 mt-2">End time must be after start time</p>
                )}
              </div>
            )}
          </div>

          {/* Day Selector */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 text-sm uppercase tracking-wide">
              Select Days ({getSelectedCount()} selected)
            </h3>
            <div className="grid grid-cols-7 gap-2">
              {weekDays.map((date, index) => {
                const isSelected = selectedDays[index];
                const isToday = isSameDay(date, new Date());
                
                return (
                  <button
                    key={index}
                    onClick={() => toggleDay(index)}
                    className={`aspect-square rounded-xl text-xs font-semibold transition-all ${
                      isSelected
                        ? 'bg-teal-500 text-white shadow-lg transform scale-105'
                        : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
                    } ${isToday ? 'ring-2 ring-gold-400' : ''}`}
                  >
                    <div className="flex flex-col items-center justify-center h-full p-1">
                      <div className="text-[10px] font-medium">{dayLabels[index].slice(0, 3)}</div>
                      <div className="text-base font-bold">{format(date, 'd')}</div>
                      {isSelected && (
                        <Check className="w-3 h-3" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                // Select weekdays (Tue-Fri + Mon since we start on Tuesday)
                const weekdaySelection = {};
                [0, 1, 2, 3, 6].forEach(i => {
                  weekdaySelection[i] = activeShift;
                });
                setSelectedDays(weekdaySelection);
              }}
              className="flex-1 py-2 px-3 bg-stone-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-stone-200 transition-colors"
            >
              Weekdays Only
            </button>
            <button
              onClick={() => setSelectedDays({})}
              className="flex-1 py-2 px-3 bg-stone-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-stone-200 transition-colors"
            >
              Clear All
            </button>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!isValidSubmission() || isSubmitting}
            className={`w-full py-4 rounded-xl font-semibold text-white transition-all ${
              !isValidSubmission() || isSubmitting
                ? 'bg-stone-300 cursor-not-allowed'
                : 'bg-gradient-to-r from-teal-500 to-teal-700 hover:from-teal-600 hover:to-teal-800 shadow-lg hover:shadow-xl'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Adding to Calendar...
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                Add {getSelectedCount()} Shift{getSelectedCount() !== 1 ? 's' : ''} to Calendar
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickWorkSchedule;